import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Filter, Trash2, Check } from "lucide-react";
import MultiSelect from "../common/MultiSelect";
import DateInputBR from "../common/DateInputBR";
import { publicationsDjeService } from "../../services/publicationsDjeService";

export default function FilterSidebarDje({
  isOpen,
  onClose,
  // Classificações
  selectedClassifications,
  setSelectedClassifications,
  availableClassifications,
  classificationsLoading,
  // Score Range
  selectedScoreRange,
  setSelectedScoreRange,
  // Tribunal
  selectedTribunal,
  setSelectedTribunal,
  availableTribunals,
  tribunalsLoading,
  // Esfera de Justiça
  selectedJusticeSphere,
  setSelectedJusticeSphere,
  availableJusticeSpheres,
  justiceSpheresLoading,
  // Estado
  selectedState,
  setSelectedState,
  availableStates,
  statesLoading,
  // Polo passivo
  selectedPoloPassivo,
  setSelectedPoloPassivo,
  // Datas
  selectedDateStart,
  setSelectedDateStart,
  selectedDateEnd,
  setSelectedDateEnd,
  // Ações
  onApplyFilters,
  onClearFilters,
  hasActiveFilters,
}) {
  const sidebarRef = useRef(null);

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const classificationOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    for (const item of availableClassifications || []) {
      const name = typeof item === "string" ? item : item?.name;
      const label = typeof name === "string" ? name.trim() : "";
      if (!label || seen.has(label)) continue;
      seen.add(label);
      options.push({ value: label, label });
    }

    return options;
  }, [availableClassifications]);

  if (!isOpen) return null;

  const scoreRanges = [
    { value: "excepcional", label: "Excepcional (76-100)" },
    { value: "high", label: "Provável (66-75)" },
    { value: "medium", label: "Possível (41-65)" },
    { value: "low", label: "Remoto (11-40)" },
  ];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className="fixed right-0 top-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <Filter className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Filtros</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Classificações */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Classificações
            </label>
            <MultiSelect
              options={classificationOptions}
              value={selectedClassifications}
              onChange={setSelectedClassifications}
              placeholder="Selecione classificações"
              loading={classificationsLoading}
              disabled={classificationsLoading}
              searchable
            />
          </div>

          {/* Score Range */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Faixa de Score
            </label>
            <select
              value={selectedScoreRange}
              onChange={(e) => setSelectedScoreRange(e.target.value)}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos os scores</option>
              {scoreRanges.map((range) => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-600 mt-1">
              Filtrar por relevância do score
            </p>
          </div>

          {/* Tribunal */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Tribunal
            </label>
            <select
              value={selectedTribunal}
              onChange={(e) => setSelectedTribunal(e.target.value)}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:text-gray-700 disabled:bg-gray-100 disabled:opacity-100"
              disabled={tribunalsLoading}
            >
              <option value="">
                {tribunalsLoading ? "Carregando..." : "Todos os tribunais"}
              </option>
              {availableTribunals.map((tribunal) => (
                <option key={tribunal} value={tribunal}>
                  {tribunal}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-600 mt-1">
              Filtrar por tribunal específico
            </p>
          </div>

          {/* Esfera de Justiça */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Esfera de Justiça
            </label>
            <select
              value={selectedJusticeSphere}
              onChange={(e) => setSelectedJusticeSphere(e.target.value)}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:text-gray-700 disabled:bg-gray-100 disabled:opacity-100"
              disabled={justiceSpheresLoading}
            >
              <option value="">
                {justiceSpheresLoading ? "Carregando..." : "Todas as esferas"}
              </option>
              {availableJusticeSpheres.map((sphere) => (
                <option key={sphere} value={sphere}>
                  {sphere}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-600 mt-1">
              Filtrar por esfera (Federal, Estadual, etc.)
            </p>
          </div>

          <PassivePoleAutocomplete
            value={selectedPoloPassivo}
            onChange={setSelectedPoloPassivo}
          />

          {/* Estado */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Estado
            </label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:text-gray-700 disabled:bg-gray-100 disabled:opacity-100"
              disabled={statesLoading}
            >
              <option value="">
                {statesLoading ? "Carregando..." : "Todos os estados"}
              </option>
              {availableStates.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-600 mt-1">
              Filtrar por estado
            </p>
          </div>

          {/* Período */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Período de Disponibilização
            </label>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Data Início
                </label>
                <DateInputBR
                  value={selectedDateStart}
                  onChange={(e) => setSelectedDateStart(e.target.value)}
                  className="w-full px-3 py-2 bg-white text-gray-900 placeholder:text-gray-500 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Data Fim
                </label>
                <DateInputBR
                  value={selectedDateEnd}
                  onChange={(e) => setSelectedDateEnd(e.target.value)}
                  className="w-full px-3 py-2 bg-white text-gray-900 placeholder:text-gray-500 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Filtrar por intervalo de datas
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 space-y-3">
          <button
            onClick={() => {
              onApplyFilters();
              onClose();
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <Check className="w-4 h-4" />
            Aplicar Filtros
          </button>

          <button
            type="button"
            onClick={() => {
              onClearFilters();
              onClose();
            }}
            disabled={!hasActiveFilters}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-200"
          >
            <Trash2 className="w-4 h-4" />
            Limpar Filtros
          </button>
        </div>
      </div>
    </>
  );
}

function PassivePoleAutocomplete({ value, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const requestId = useRef(0);
  const skipNextFetch = useRef(false);

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }

    const term = value.trim();
    if (term.length < 2) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }

    const currentRequest = requestId.current + 1;
    requestId.current = currentRequest;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const names = await publicationsDjeService.suggestPassivePoles(term);
        if (requestId.current !== currentRequest) return;
        const list = Array.isArray(names) ? names : [];
        setSuggestions(list);
        setOpen(list.length > 0);
        setActiveIndex(-1);
      } catch {
        if (requestId.current !== currentRequest) return;
        setSuggestions([]);
        setOpen(false);
      } finally {
        if (requestId.current === currentRequest) setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [value]);

  const selectSuggestion = (name) => {
    skipNextFetch.current = true;
    onChange(name);
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (!open || suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) =>
        current <= 0 ? suggestions.length - 1 : current - 1,
      );
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[activeIndex]);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div>
      <label
        htmlFor="polo-passivo-filtro"
        className="block text-sm font-semibold text-gray-700 mb-2"
      >
        Polo passivo
      </label>
      <input
        id="polo-passivo-filtro"
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder="Nome da parte..."
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls="polo-passivo-sugestoes"
        aria-autocomplete="list"
        className="w-full px-3 py-2 bg-white text-gray-900 placeholder:text-gray-500 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      {loading && value.trim().length >= 2 && (
        <p className="text-xs text-gray-500 mt-1">Buscando polos...</p>
      )}
      {open && suggestions.length > 0 && (
        <ul
          id="polo-passivo-sugestoes"
          role="listbox"
          className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-sm"
        >
          {suggestions.map((name, index) => (
            <li key={name} role="option" aria-selected={index === activeIndex}>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectSuggestion(name)}
                className={`w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-blue-50 ${
                  index === activeIndex ? "bg-blue-50" : ""
                }`}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-gray-600 mt-1">
        Digite para ver os polos passivos correspondentes
      </p>
    </div>
  );
}

